import { useMemo, useRef, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog as DialogPrimitive } from 'radix-ui'
import {
  Bell,
  Building2,
  ChevronLeft,
  ChevronRight,
  FolderOpen,
  Plug,
  User,
  Users,
  XIcon,
  type LucideIcon,
} from 'lucide-react'
import {
  Dialog,
  DialogDescription,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from '@/components/ui/dialog'
import { MembersPage } from '@/components/members-page'
import { ProjectSettingsPage } from '@/components/project-settings-page'
import { useIsMobile } from '@/hooks/use-mobile'
import { useAuthState } from '@/lib/auth-state'
import { useProject } from '@/lib/project-context'
import {
  ACCOUNT_PANES,
  PROJECT_PANES,
  sectionForUser,
  type SettingsPane,
  type SettingsSection,
} from '@/lib/settings-dialog'
import { cn } from '@/lib/utils'
import { OrganizationSettings } from './organization-settings'
import { PluginsSettings } from './plugins-settings'
import { NotificationSettings, ProfileSettings } from './profile-settings'
import { useSettingsDialog } from './use-settings-dialog'

const PANE_ICONS: Record<SettingsPane, LucideIcon> = {
  profile: User,
  notifications: Bell,
  project: FolderOpen,
  members: Users,
  plugins: Plug,
  organization: Building2,
}

/**
 * The settings dialog of a project (board h39z), mounted once in the project
 * layout and driven by `?settings=<section>`. The page under it stays mounted.
 */
export function SettingsDialog() {
  const { section, switchTo, close } = useSettingsDialog()
  const { project, projectId } = useProject()
  const { me } = useAuthState()

  // Same gate as the old settings tab strip: org owner/admin of the project's org.
  const orgRole = me?.memberships.organizations.find(
    (m) => m.organizationId === project.organizationId
  )?.role
  const canSeeOrganization = orgRole === 'owner' || orgRole === 'admin'
  // Board wwfu: the organization's mcpPlugins flag; every member sees the pane (read-only for members).
  const canSeePlugins = project.features.mcpPlugins

  return (
    <SettingsDialogView
      section={section}
      project={{ name: project.name, canSeeOrganization, canSeePlugins }}
      onSwitch={switchTo}
      onClose={close}
      renderPane={(pane) => <SettingsPaneContent pane={pane} projectId={projectId} />}
    />
  )
}

/** The content of one section — the old pages' components, unchanged. */
export function SettingsPaneContent({
  pane,
  projectId,
}: {
  pane: SettingsPane
  projectId: string | null
}) {
  switch (pane) {
    case 'profile':
      return <ProfileSettings />
    case 'notifications':
      return <NotificationSettings />
    case 'project':
      return projectId ? <ProjectSettingsPage projectId={projectId} /> : null
    case 'members':
      return projectId ? <MembersPage projectId={projectId} /> : null
    case 'plugins':
      return projectId ? <PluginsSettings projectId={projectId} /> : null
    case 'organization':
      return projectId ? <OrganizationSettings /> : null
  }
}

export interface SettingsDialogViewProps {
  /** From the URL; undefined = closed. */
  section: SettingsSection | undefined
  /** null = no project (the `/settings` fallback): only the account sections. */
  project: { name: string; canSeeOrganization: boolean; canSeePlugins?: boolean } | null
  onSwitch: (section: SettingsSection) => void
  onClose: () => void
  renderPane: (pane: SettingsPane) => ReactNode
}

/**
 * One Radix Dialog, responsive by CSS: a centred two-column card with a
 * blurred backdrop on desktop, a full-screen sheet with a list screen and a
 * section screen on phones. `useIsMobile()` only decides what `menu` shows.
 */
export function SettingsDialogView({
  section,
  project,
  onSwitch,
  onClose,
  renderPane,
}: SettingsDialogViewProps) {
  const { t } = useTranslation('settings')
  const isMobile = useIsMobile()
  const open = section !== undefined

  // Keep showing the last section while the close animation runs.
  const contentRef = useRef<HTMLDivElement>(null)
  const lastSection = useRef<SettingsSection>('menu')
  if (section) lastSection.current = section
  const requested = section ?? lastSection.current

  const groups = useMemo(() => {
    const result: { key: string; label: string; panes: SettingsPane[] }[] = [
      { key: 'account', label: t('dialog.account'), panes: [...ACCOUNT_PANES] },
    ]
    if (project) {
      result.push({
        key: 'project',
        label: project.name,
        panes: PROJECT_PANES.filter(
          (p) =>
            (p !== 'organization' || project.canSeeOrganization) &&
            (p !== 'plugins' || project.canSeePlugins === true)
        ),
      })
    }
    return result
  }, [project, t])

  let shown = sectionForUser(requested, {
    isMobile,
    canSeeOrganization: project?.canSeeOrganization ?? false,
    canSeePlugins: project?.canSeePlugins ?? false,
  })
  if (!project && shown !== 'menu' && !(ACCOUNT_PANES as readonly string[]).includes(shown)) {
    shown = 'profile'
  }
  const pane = shown === 'menu' ? null : shown

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogPortal>
        <DialogOverlay
          data-slot="settings-dialog-overlay"
          className="desktop:bg-black/40 desktop:backdrop-blur-sm desktop:dark:bg-black/60"
        />
        <DialogPrimitive.Content
          ref={contentRef}
          data-slot="settings-dialog"
          data-section={shown}
          // Focus the dialog itself, not the first list item: a ring on Profil (or ←) reads as a
          // second selection. Tab still walks list → pane → close.
          onOpenAutoFocus={(event) => {
            event.preventDefault()
            contentRef.current?.focus()
          }}
          className={cn(
            'fixed inset-0 z-50 flex h-dvh w-full flex-col overflow-hidden bg-background outline-none duration-200',
            'data-[state=open]:animate-in data-[state=open]:slide-in-from-bottom data-[state=closed]:animate-out data-[state=closed]:slide-out-to-bottom',
            'desktop:inset-auto desktop:top-1/2 desktop:left-1/2 desktop:h-[min(85dvh,760px)] desktop:w-[calc(100%-4rem)] desktop:max-w-5xl desktop:-translate-x-1/2 desktop:-translate-y-1/2 desktop:rounded-xl desktop:border desktop:shadow-2xl',
            'desktop:data-[state=open]:fade-in-0 desktop:data-[state=open]:zoom-in-95 desktop:data-[state=open]:slide-in-from-bottom-0 desktop:data-[state=closed]:fade-out-0 desktop:data-[state=closed]:zoom-out-95 desktop:data-[state=closed]:slide-out-to-bottom-0'
          )}
        >
          <DialogTitle className="sr-only">{t('dialog.title')}</DialogTitle>
          <DialogDescription className="sr-only">{t('dialog.description')}</DialogDescription>

          {/* Phone header: the list screen has the title, a section has ← + its name. */}
          <header className="flex h-14 shrink-0 items-center gap-1 border-b border-border px-2 desktop:hidden">
            {pane ? (
              <button
                type="button"
                data-slot="settings-back"
                aria-label={t('dialog.back')}
                onClick={() => onSwitch('menu')}
                className="flex size-10 items-center justify-center rounded-lg text-foreground/70 hover:bg-accent hover:text-foreground"
              >
                <ChevronLeft className="size-5" />
              </button>
            ) : null}
            <h2 className={cn('flex-1 truncate text-base font-semibold', !pane && 'pl-2')}>
              {pane ? t(`nav.${pane}`) : t('dialog.title')}
            </h2>
            <CloseButton label={t('dialog.close')} />
          </header>

          <div className="flex min-h-0 flex-1">
            <nav
              data-slot="settings-nav"
              aria-label={t('dialog.title')}
              className={cn(
                'w-full shrink-0 overflow-y-auto p-2 desktop:w-56 desktop:border-r desktop:border-border desktop:bg-muted/30 desktop:p-3',
                pane && 'hidden desktop:block'
              )}
            >
              <p className="hidden px-3 pt-1 pb-3 text-base font-semibold desktop:block">
                {t('dialog.title')}
              </p>
              {groups.map((group) => (
                <div key={group.key} className="mb-4 last:mb-0">
                  <p className="truncate px-3 pt-2 pb-1 text-xs font-medium text-muted-foreground">
                    {group.label}
                  </p>
                  <ul className="space-y-0.5">
                    {group.panes.map((item) => {
                      const Icon = PANE_ICONS[item]
                      const active = item === shown
                      return (
                        <li key={item}>
                          <button
                            type="button"
                            data-pane={item}
                            aria-current={active ? 'page' : undefined}
                            onClick={() => onSwitch(item)}
                            className={cn(
                              'flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left text-sm font-medium transition-colors desktop:py-2',
                              'text-foreground desktop:text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                              active &&
                                'bg-accent text-accent-foreground desktop:text-accent-foreground'
                            )}
                          >
                            <Icon className="size-4 shrink-0" />
                            <span className="flex-1 truncate">{t(`nav.${item}`)}</span>
                            <ChevronRight className="size-4 shrink-0 text-muted-foreground desktop:hidden" />
                          </button>
                        </li>
                      )
                    })}
                  </ul>
                </div>
              ))}
            </nav>

            {pane && (
              <section data-slot="settings-pane" className="flex min-w-0 flex-1 flex-col">
                <header className="hidden h-14 shrink-0 items-center gap-2 border-b border-border pr-3 pl-6 desktop:flex">
                  <h2 className="flex-1 truncate text-base font-semibold">{t(`nav.${pane}`)}</h2>
                  <CloseButton label={t('dialog.close')} />
                </header>
                <div className="min-h-0 flex-1 overflow-auto overscroll-contain">
                  {renderPane(pane)}
                </div>
              </section>
            )}
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  )
}

function CloseButton({ label }: { label: string }) {
  return (
    <DialogPrimitive.Close
      data-slot="settings-close"
      aria-label={label}
      className="flex size-10 items-center justify-center rounded-lg text-foreground/70 hover:bg-accent hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-hidden desktop:size-8"
    >
      <XIcon className="size-4" />
    </DialogPrimitive.Close>
  )
}
